import { Clipboard } from '@angular/cdk/clipboard';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AsyncJob } from '../../models/async-job.model';
import { FormInstanceSaveMetrics } from '../../models/export-import/form-instance-save-metrics.model';
import { AsyncJobService } from '../../services/async-job.service';
import { FormInstanceAsyncService } from '../../services/form-instance-async.service';
import { AsyncJobDialogBase } from '../async-job-base/async-job-base.dialog';
import { FormInstanceService } from '../../services/form-instance.service';
import { CommunicationService } from '../../services/communication.service';
import { FormInstanceOperationsDialogInitInfo, FormInstanceOperationsDialogCloseInfo } from './form-instance-operations.extras';
import { IFormDialogOperation } from './operations/i-form-dialog-operation';
import { FormInstanceOperationsEnum } from './form-instance-operations.enum';
import { OpenFormOperation } from './operations/open-form.operation';
import { SaveFormOperation } from './operations/save-form.operation';
import { TransitionFormOperation } from './operations/transition-form.operation';

/*
    See https://community-dc.max.gov/x/Zoytnw for some background on this

    This dialog displays progress information in three scenarios:
    1) When a FormInstance is being loaded
    2) When a FormInstance is being saved
    3) When a FormInstance is being transitioned to another workflow state

    For #1 the act of opening the FormInstance is trigged from outside of the dialog, whereas for #2 and #3 the dialog initiates the actual saving/transitioning 

    Rather than relying on a bunch of if statements to manage these different scanarios the approach we use elsewhere has been employed in
    which we have an interface (in this case IFormDialogOperation) and implementations of that interface (OpenFormOperation, SaveFormOperation and TransitionFormOperaion)
    such that the only if logic we need to execute in the dialog itself is to decide which implementation to use. (That logic is in the getOperation() method and
    it gets called from ngOnInit)

    The IFormDialogOperation implementations know what messages should be displayed in the dialog, and what performing the operation looks like.
*/


@Component({
    selector: 'app-form-instance-operations-dlg',
    templateUrl: './form-instance-operations.dialog.html',
    styleUrls: [
        '../async-job-base/async-job-base.dialog.scss',
        './form-instance-operations.dialog.scss'
    ],
    standalone: false
})
export class FormInstanceOperationsDialog extends AsyncJobDialogBase implements OnInit, OnDestroy {
    // Properties.
    private asyncJob: AsyncJob = null;
    private formInstanceId: number = 0;
    private saveMetrics: FormInstanceSaveMetrics = null;
    private viewFormSaveLog: boolean = false;
    private messageFromClientBeforeServerCall: string = "";
    private messageFromClientAfterServerCall: string = "";
    private startMonitoringFormOpenAt: string;
    private formOpenMessagesFromServer: string = '';
    private formDialogOperation: IFormDialogOperation;

    // Constructor.
    public constructor(public dialogRef: MatDialogRef<FormInstanceOperationsDialog>,
        @Inject(MAT_DIALOG_DATA) public initInfo: FormInstanceOperationsDialogInitInfo,
        private clipboard: Clipboard,
        public dialog: MatDialog,
        private communicationService: CommunicationService,
        public asyncFormInstanceSaveService: FormInstanceAsyncService,
        private formInstanceService: FormInstanceService,
        private asyncJobService2: AsyncJobService) {
        super(asyncJobService2);

        dialogRef.disableClose = true; // Disable closing the dialogue when the user clicks on the page.

        this.formDialogOperation = this.getOperation(initInfo.operation);
    }

    // Life cycle methods.
    public ngOnInit(): void {
        // if the operation should be performed with human intervention (like clicking an ok button) then just go ahead and perform it now
        // (The open Form operation is an example of this)
        if (this.formDialogOperation.performAutomatically)
            this.formDialogOperation.performOperation();
    }

    public ngOnDestroy(): void {
        clearInterval(this.IntervalTimeoutId);
    }

    // For operations that require the user to click a button or something to start the operation (such as transitioning a Form)
    public startOperationManually(): void {
        this.formDialogOperation.performOperation();
    }

    public get ShowTimeElapsed(): boolean {
        return this.formDialogOperation.showElapsedTime;
    }

    public get Title() {
        return this.formDialogOperation.title;
    }

    public get ViewFormSaveLog() {
        return this.formDialogOperation.alwaysShowLog || this.viewFormSaveLog;
    }

    public toggleViewFormSaveLog() {
        this.viewFormSaveLog = !this.viewFormSaveLog;
    }

    public get DismissButtonTitle(): string {
        return this.formDialogOperation.dismissButtonTitle;
    }

    public get Status(): string {
        let status = this.formDialogOperation.Status;
        return status;
    }

    public showDismissButton(): boolean {
        return this.formDialogOperation.showDismissButton;
    }

    public showGoToSiteHomePageButton(): boolean {
        return this.formDialogOperation.showGoToSiteHomePageButton;
    }

    public showStartOperationManuallyButton(): boolean {
        return this.formDialogOperation.showStartOperationManuallyButton;
    }

    public showDismissWithReloadButton(): boolean {
        return this.formDialogOperation.showDismissWithReloadButton;
    }

    public disableDismissButton(): boolean {
        return this.formDialogOperation.disableDismissButton();
    }
    private messageReceievedFromServer: boolean = false;
    public get ExecutionSummaryText(): string {
        let summaryText: string = 'Starting...';
        
        if (this.jobErrorOccurred && this.jobErrorText?.trim() != '') {
            summaryText = this.jobErrorText;

            if (this.saveMetrics != null) {
                if (this.saveMetrics.getErrorLog()?.trim() != '') {
                    summaryText += "\r\n";
                    summaryText += this.saveMetrics.getErrorLog();
                } else if (this.saveMetrics.getWarningLog()?.trim() != '') {
                    summaryText += "\r\n";
                    summaryText += this.saveMetrics.getWarningLog();
                }
            }
        } else if (this.saveMetrics != null) {
            summaryText = this.saveMetrics.getJobSummaryText();
        }

        summaryText = `${summaryText}${this.messageFromClientBeforeServerCall}`;

        // Currently commented out because trying to add logs from the server and blend them with logs before and after
        // the server was not working consistently. Given that was I told not spend long on this, I decided to comment it out
        // as it didn't add much anyway.

        //if (this.formOpenMessagesFromServer?.trim() != '' && this.messageFromClientAfterServerCall?.trim() == '') {
        //    if (!this.messageReceievedFromServer) {
        //        summaryText = `${summaryText}\n`;
        //        this.messageReceievedFromServer = true;
        //    }
        //    summaryText = `${summaryText}${this.formOpenMessagesFromServer}`;
        //}

        summaryText = `${summaryText}${this.messageFromClientAfterServerCall}`;

        return summaryText;
    }

    // Handle control events.

    public dismissClicked(reloadAfter: boolean): void {
        const closeInfo: FormInstanceOperationsDialogCloseInfo = {
            reloadAfterClose: reloadAfter,
            userNoLongerHasAccess: this.formDialogOperation.UserNoLongerAccessToFormInstance
        };
        this.dialogRef.close(closeInfo);
    }

    public goToSiteHomePage(): void {
        window.open(`/site/${this.initInfo.formInstance.dataCollectionId}/home-page`, '_self');
    }

    public copyJobErrorTextToClipboard(): void {
        if (this.jobErrorText != null) {
            this.clipboard.copy(this.jobErrorText);
        }
    }

    public jobIsCompleted() {
        return this.jobCompletedMessageText != null && this.jobCompletedMessageText.trim() != '';
    }

    public startMonitoringJob(asyncJob: AsyncJob, jobCompletedCallback: (asyncJob: AsyncJob) => void, jobUpdatedCallback: (asyncJob: AsyncJob) => void = null) {
        super.startMonitoringJob(asyncJob, jobCompletedCallback, jobCompletedCallback);
    }

    // Implement a base class's abstract method.
    public get ComponentClassName(): string {
        return 'FormInstanceOperationsDialog';
    }

    // Factory method that returns the appropriate operation
    // Note that "this" is passed into the constructors so that the operations have access to the dialog and its injected data
    private getOperation(operation: string): IFormDialogOperation {
        if (operation == FormInstanceOperationsEnum.OPEN) {
            return new OpenFormOperation(this);
        } else if (operation == FormInstanceOperationsEnum.SAVE) {
            return new SaveFormOperation(this);
        } else if (operation == FormInstanceOperationsEnum.SAVE_WITH_TRANSITION) {
            return new TransitionFormOperation(this);
        }
    }

    // Getters/Setters (Used by Operations implementations to access properties of the dialog)
    public get FormInstanceId() {
        return this.formInstanceId;
    }
    public set FormInstanceId(value: number) {
        this.formInstanceId = value;
    }

    public get JobInProgress() {
        return this.jobInProgress;
    }

    public get JobErrorOccurred() {
        return this.jobErrorOccurred;
    }

    public get TimeoutTimeInMilliseconds() {
        return this.timeoutTimeInMilliseconds;
    }

    public get TimeoutsSinceJobInitiated() {
        return this.timeoutsSinceJobInitiated;
    }
    public set TimeoutsSinceJobInitiated(value: number) {
        this.timeoutsSinceJobInitiated = value;
    }

    public get IntervalTimeoutId() {
        return this.intervalTimeoutId;
    }
    public set IntervalTimeoutId(value: any) {
        this.intervalTimeoutId = value;
    }

    public get MessageFromClientBeforeServerCall() {
        return this.messageFromClientBeforeServerCall;
    }
    public set MessageFromClientBeforeServerCall(value: string) {
        this.messageFromClientBeforeServerCall = value;
    }

    public get MessageFromClientAfterServerCall() {
        return this.messageFromClientAfterServerCall;
    }
    public set MessageFromClientAfterServerCall(value: string) {
        this.messageFromClientAfterServerCall = value;
    }

    public get FormOpenMessagesFromServer() {
        return this.formOpenMessagesFromServer;
    }
    public set FormOpenMessagesFromServer(value: string) {
        this.formOpenMessagesFromServer = value;
    }

    public get StartMonitoringFormOpenAt() {
        return this.startMonitoringFormOpenAt;
    }
    public set StartMonitoringFormOpenAt(value: string) {
        this.startMonitoringFormOpenAt = value;
    }

    public set AsyncJob(value: AsyncJob) {
        this.asyncJob = value;
    }
    public get AsyncJob() {
        return this.asyncJob;
    }

    public get AsyncJobInfo() {
        return this.asyncJobInfo;
    }

    public set SaveMetrics(value: FormInstanceSaveMetrics) {
        this.saveMetrics = value;
    }
}

